import type { SummaryFieldDefinition, SummarySectionDefinition } from "../../lib/formSubmissionSummary";
import type { ConfiguredField, ConfiguredFormSection, ConfiguredServiceForm } from "./configuredServiceForms";

type DirectoryVisibility = "Public Client Service Form" | "Private Direct Link" | "Admin Only";

export interface AdminFormsDirectoryEntry {
  formName: string;
  category: string;
  routeUrl: string;
  visibility: DirectoryVisibility;
  purpose: string;
}

const serviceInterestOptions = [
  "RSVP Hub",
  "Memory Hub",
  "Archive Hub",
  "Memory Media Vault",
  "Signature Song",
  "Signature Highlight Reel",
  "Bundle / Package",
  "Not Sure Yet",
];

const publicServiceOptions = [
  "RSVP Hub",
  "Memory Hub",
  "Archive Hub",
  "Memory Media Vault",
  "Signature Song",
  "Signature Highlight Reel",
];

const yesNoOptions = ["Yes", "No"];
const yesNoPendingOptions = ["Yes", "No", "Pending"];
const preferredContactOptions = ["Email", "Phone", "Text"];
const priorityOptions = ["Low", "Normal", "High", "Rush"];
const supportPriorityOptions = ["Low", "Normal", "High", "Urgent"];
const projectStatusOptions = [
  "New Inquiry",
  "Discovery Scheduled",
  "Discovery Completed",
  "Proposal Approved to Send",
  "Proposal Sent",
  "Agreement / Invoice Sent",
  "Invoice Created",
  "Invoice Sent",
  "Partial Payment Received",
  "Awaiting Final Payment",
  "Paid In Full",
  "Payment Received",
  "Welcome Email Sent",
  "Onboarding Sent",
  "Onboarding Complete",
  "Service Form Received",
  "Kickoff Call Completed",
  "Build In Progress",
  "Awaiting Client Content",
  "On Hold",
  "Internal Review",
  "Awaiting Client Review",
  "In Production",
  "Client Review Sent",
  "Revisions",
  "Final Approval Pending",
  "Delivered",
  "Follow-Up Sent",
  "Testimonial Requested",
  "Referral Invitation Sent",
  "Completed",
];

const requiredLabels = {
  clientName: "Client Name is required.",
  clientEmail: "Client Email is required.",
  servicePurchased: "Service Purchased is required.",
  projectName: "Project Name is required.",
  serviceType: "Service Type is required.",
  projectStatus: "Project Status is required.",
  partnerName: "Partner Name is required.",
  partnerType: "Partner Type is required.",
  partnerEmail: "Partner Email is required.",
  partnerCode: "Partner Code is required.",
  codeStatus: "Code Status is required.",
  referredClientName: "Referred Client Name is required.",
  referredServiceInterest: "Referred Service Interest is required.",
  referralDate: "Referral Date is required.",
  clientBookingStatus: "Client Booking Status is required.",
  payoutStatus: "Payout Status is required.",
  internalTrackingConfirmation: "Please confirm this record is for internal Kreatethedate tracking only.",
  serviceCompleted: "Service Completed is required.",
  requestDetails: "Request Details are required.",
};

const internalConfirmationField: ConfiguredField = {
  type: "checkbox",
  name: "internalTrackingConfirmation",
  label: "I confirm this record is for internal Kreatethedate tracking only.",
  required: true,
};

function adminForm(options: Omit<ConfiguredServiceForm, "summarySections" | "requiredLabels"> & { requiredLabels?: Record<string, string> }) {
  return {
    ...options,
    saveEmailField: options.saveEmailField || "adminResumeEmail",
    requiredLabels: { ...requiredLabels, ...(options.requiredLabels || {}) },
    summarySections: summarySectionsFrom(options.sections),
  } satisfies ConfiguredServiceForm;
}

function summarySectionsFrom(sections: ConfiguredFormSection[]): SummarySectionDefinition[] {
  return sections
    .map((section) => ({
      title: section.title,
      fields: section.fields.map(summaryFieldFrom).filter((field): field is SummaryFieldDefinition => Boolean(field)),
    }))
    .filter((section) => section.fields.length > 0);
}

function summaryFieldFrom(field: ConfiguredField): SummaryFieldDefinition | null {
  if (!field.name || !field.label || field.type === "notice") return null;

  if (field.type === "date") return { name: field.name, label: field.label, type: "date" };
  if (field.type === "checkbox") return { name: field.name, label: field.label, type: "checkbox" };
  if (field.type === "checkboxGroup") return { name: field.name, label: field.label, type: "checkboxGroup" };
  if (field.type === "file") return { name: field.name, label: field.label, type: "file" };
  return { name: field.name, label: field.label };
}

const discoveryCallNotesSections: ConfiguredFormSection[] = [
  {
    id: "discovery-overview",
    title: "Discovery Call Details",
    fields: [
      { type: "text", name: "clientName", label: "Client Name", required: true, autoComplete: "name" },
      { type: "email", name: "clientEmail", label: "Client Email", required: true, autoComplete: "email" },
      { type: "tel", name: "clientPhone", label: "Client Phone", autoComplete: "tel" },
      { type: "text", name: "eventType", label: "Event Type" },
      { type: "date", name: "eventDate", label: "Event Date" },
      {
        type: "checkboxGroup",
        name: "serviceInterest",
        label: "Service Interest",
        options: serviceInterestOptions,
        className: "md:col-span-2",
      },
    ],
  },
  {
    id: "call-notes",
    title: "Call Notes & Recommendations",
    fields: [
      { type: "textarea", name: "clientGoals", label: "Client Goals", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "painPoints", label: "Pain Points / Concerns", rows: 5, className: "md:col-span-2" },
      { type: "text", name: "budgetRange", label: "Budget Range" },
      { type: "text", name: "timelineNeeded", label: "Timeline Needed" },
      { type: "textarea", name: "recommendedService", label: "Recommended Service", rows: 4, className: "md:col-span-2" },
      { type: "textarea", name: "addOnOpportunities", label: "Add-On Opportunities", rows: 4, className: "md:col-span-2" },
      { type: "radio", name: "followUpNeeded", label: "Follow-Up Needed?", options: yesNoOptions },
      { type: "date", name: "followUpDate", label: "Follow-Up Date" },
      { type: "textarea", name: "internalNotes", label: "Internal Notes", rows: 6, className: "md:col-span-2" },
      {
        type: "radio",
        name: "discoveryCallOutcome",
        label: "Discovery Call Outcome",
        options: ["Proposal Needed", "Not a Fit", "Follow-Up Later", "Booked / Ready for Proposal"],
        className: "md:col-span-2",
      },
    ],
  },
];

export const discoveryCallNotesFormConfig = adminForm({
  formKey: "admin-discovery-call-notes",
  formPath: "/admin/discovery-call-notes",
  formTitle: "Discovery Call Notes",
  setupTitle: "Discovery Call Notes",
  eyebrow: "Admin Only",
  heroHeading: "Discovery Call Notes",
  heroSubheading: "Record internal call notes, client goals, service interest, recommendations, and follow-up needs.",
  metaDescription: "Internal Kreatethedate discovery call notes form.",
  submitLabel: "Submit Discovery Notes",
  requiredMessage: "Please complete all required fields before submitting Discovery Call Notes.",
  thankYouMessage: "Your Kreatethedate Discovery Call Notes have been received.",
  subjectNameField: "clientName",
  clientNameField: "clientName",
  eventNameField: "eventType",
  eventDateField: "eventDate",
  eventNameLabel: "Event type",
  sections: discoveryCallNotesSections,
});

const kickoffCallPrepSections: ConfiguredFormSection[] = [
  {
    id: "kickoff-overview",
    title: "Kickoff Call Preparation",
    fields: [
      { type: "text", name: "clientName", label: "Client Name", required: true, autoComplete: "name" },
      { type: "text", name: "servicePurchased", label: "Service Purchased", required: true },
      { type: "text", name: "eventName", label: "Event Name / Honoree Name" },
      { type: "date", name: "eventDate", label: "Event Date" },
      { type: "radio", name: "paymentConfirmed", label: "Payment Confirmed?", options: yesNoPendingOptions },
      { type: "radio", name: "agreementSigned", label: "Agreement Signed?", options: yesNoPendingOptions },
      { type: "radio", name: "serviceFormSubmitted", label: "Service Form Submitted?", options: yesNoPendingOptions },
      { type: "radio", name: "requiredFilesReceived", label: "Required Files Received?", options: yesNoPendingOptions },
      { type: "textarea", name: "missingInformation", label: "Missing Information", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "keyCreativeDirection", label: "Key Creative Direction", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "kickoffQuestions", label: "Questions to Ask During Kickoff Call", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "risksTimelineConcerns", label: "Risks / Timeline Concerns", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "adminNotes", label: "Admin Notes", rows: 6, className: "md:col-span-2" },
    ],
  },
];

export const kickoffCallPrepFormConfig = adminForm({
  formKey: "admin-kickoff-call-prep",
  formPath: "/admin/kickoff-call-prep",
  formTitle: "Kickoff Call Prep",
  setupTitle: "Kickoff Call Prep",
  eyebrow: "Admin Only",
  heroHeading: "Kickoff Call Prep",
  heroSubheading: "Prepare the internal checklist, creative direction, missing details, and project risks before kickoff.",
  metaDescription: "Internal Kreatethedate kickoff call preparation form.",
  submitLabel: "Submit Kickoff Prep",
  requiredMessage: "Please complete all required fields before submitting Kickoff Call Prep.",
  thankYouMessage: "Your Kreatethedate Kickoff Call Prep has been received.",
  subjectNameField: "clientName",
  clientNameField: "clientName",
  eventNameField: "eventName",
  eventDateField: "eventDate",
  sections: kickoffCallPrepSections,
});

const clientDirectorySections: ConfiguredFormSection[] = [
  {
    id: "client-directory",
    title: "Client Directory Entry",
    fields: [
      { type: "text", name: "clientName", label: "Client Name", required: true, autoComplete: "name" },
      { type: "email", name: "clientEmail", label: "Email", required: true, autoComplete: "email" },
      { type: "tel", name: "clientPhone", label: "Phone", autoComplete: "tel" },
      { type: "text", name: "servicePurchased", label: "Service Purchased", required: true },
      { type: "text", name: "eventName", label: "Event Name" },
      { type: "date", name: "eventDate", label: "Event Date" },
      { type: "select", name: "projectStatus", label: "Project Status", options: projectStatusOptions },
      { type: "text", name: "projectFolderLink", label: "Project Folder Link", className: "md:col-span-2" },
      { type: "text", name: "formSubmissionLink", label: "Form Submission Link", className: "md:col-span-2" },
      { type: "date", name: "kickoffCallDate", label: "Kickoff Call Date" },
      { type: "date", name: "deliveryDeadline", label: "Delivery Deadline" },
      { type: "textarea", name: "internalNotes", label: "Internal Notes", rows: 6, className: "md:col-span-2" },
    ],
  },
];

export const clientDirectoryFormConfig = adminForm({
  formKey: "admin-client-directory",
  formPath: "/admin/client-directory",
  formTitle: "Client Directory Entry",
  setupTitle: "Client Directory Entry",
  eyebrow: "Admin Only",
  heroHeading: "Client Directory Entry",
  heroSubheading: "Create or update an internal record for a Kreatethedate client, project, status, links, and timeline.",
  metaDescription: "Internal Kreatethedate client directory entry form.",
  submitLabel: "Submit Client Directory Entry",
  requiredMessage: "Please complete all required fields before submitting the Client Directory Entry.",
  thankYouMessage: "Your Kreatethedate Client Directory Entry has been received.",
  subjectNameField: "clientName",
  clientNameField: "clientName",
  eventNameField: "eventName",
  eventDateField: "eventDate",
  sections: clientDirectorySections,
});

const projectDashboardSections: ConfiguredFormSection[] = [
  {
    id: "project-dashboard",
    title: "Project Dashboard Entry",
    fields: [
      { type: "text", name: "projectName", label: "Project Name", required: true, className: "md:col-span-2" },
      { type: "text", name: "clientName", label: "Client Name", required: true, autoComplete: "name" },
      { type: "text", name: "serviceType", label: "Service Type", required: true },
      { type: "text", name: "projectStatus", label: "Project Status", required: true },
      { type: "select", name: "priority", label: "Priority", options: priorityOptions },
      { type: "date", name: "startDate", label: "Start Date" },
      { type: "date", name: "dueDate", label: "Due Date" },
      { type: "text", name: "assignedTo", label: "Assigned To" },
      { type: "radio", name: "waitingOnClient", label: "Waiting On Client?", options: yesNoOptions },
      { type: "textarea", name: "neededFromClient", label: "What Is Needed From Client?", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "nextStep", label: "Next Step", rows: 5, className: "md:col-span-2" },
      { type: "date", name: "followUpDate", label: "Follow-Up Date" },
      { type: "textarea", name: "internalNotes", label: "Internal Notes", rows: 6, className: "md:col-span-2" },
    ],
  },
];

export const projectDashboardFormConfig = adminForm({
  formKey: "admin-project-dashboard",
  formPath: "/admin/project-dashboard",
  formTitle: "Project Dashboard Entry",
  setupTitle: "Project Dashboard Entry",
  eyebrow: "Admin Only",
  heroHeading: "Project Dashboard Entry",
  heroSubheading: "Track internal project status, priority, dates, next steps, client needs, and assignment details.",
  metaDescription: "Internal Kreatethedate project dashboard entry form.",
  submitLabel: "Submit Project Dashboard Entry",
  requiredMessage: "Please complete all required fields before submitting the Project Dashboard Entry.",
  thankYouMessage: "Your Kreatethedate Project Dashboard Entry has been received.",
  subjectNameField: "projectName",
  clientNameField: "clientName",
  eventNameField: "projectName",
  eventDateField: "dueDate",
  eventNameLabel: "Project name",
  eventDateLabel: "Due date",
  sections: projectDashboardSections,
});

const partnerReferralTrackerSections: ConfiguredFormSection[] = [
  {
    id: "partner-information",
    title: "Partner Information",
    fields: [
      { type: "text", name: "partnerName", label: "Partner Name", required: true, autoComplete: "name" },
      {
        type: "select",
        name: "partnerType",
        label: "Partner Type",
        required: true,
        options: ["Paid Referral Partner", "Community Partner", "Legacy & Memorial Partner", "Event Planner Partner", "Vendor / Creative Partner", "Other"],
      },
      { type: "text", name: "businessOrganizationName", label: "Business / Organization Name", className: "md:col-span-2" },
      { type: "email", name: "partnerEmail", label: "Partner Email", required: true, autoComplete: "email" },
      { type: "tel", name: "partnerPhone", label: "Partner Phone", autoComplete: "tel" },
      { type: "text", name: "websiteSocialLink", label: "Website or Social Media Link", className: "md:col-span-2" },
      { type: "select", name: "preferredContactMethod", label: "Preferred Contact Method", options: preferredContactOptions },
    ],
  },
  {
    id: "partner-code-details",
    title: "Partner Code Details",
    fields: [
      { type: "text", name: "partnerCode", label: "Partner Code", required: true },
      { type: "select", name: "codeStatus", label: "Code Status", required: true, options: ["Active", "Pending", "Paused", "Retired"] },
      { type: "date", name: "codeStartDate", label: "Code Start Date" },
      { type: "date", name: "codeExpirationDate", label: "Code Expiration Date" },
      { type: "textarea", name: "codeUsageNotes", label: "Notes About Code Usage", rows: 5, className: "md:col-span-2" },
    ],
  },
  {
    id: "referral-details",
    title: "Referral Details",
    fields: [
      { type: "text", name: "referredClientName", label: "Referred Client Name", required: true, autoComplete: "name" },
      { type: "email", name: "referredClientEmail", label: "Referred Client Email", autoComplete: "email" },
      { type: "tel", name: "referredClientPhone", label: "Referred Client Phone", autoComplete: "tel" },
      {
        type: "select",
        name: "referredServiceInterest",
        label: "Referred Service Interest",
        required: true,
        options: [...publicServiceOptions, "Package / Bundle", "Not Sure Yet"],
      },
      { type: "date", name: "referralDate", label: "Referral Date", required: true },
      {
        type: "select",
        name: "referralReceivedMethod",
        label: "How Was the Referral Received?",
        options: ["Partner Code", "Direct Introduction", "Email", "Text", "Social Media", "Event Planner Referral", "Other"],
      },
    ],
  },
  {
    id: "booking-payment-status",
    title: "Booking & Payment Status",
    fields: [
      {
        type: "select",
        name: "clientBookingStatus",
        label: "Client Booking Status",
        required: true,
        options: ["Inquiry Received", "Discovery Call Scheduled", "Proposal Sent", "Agreement Sent", "Invoice Sent", "Paid", "Project Started", "Not Converted", "Canceled"],
      },
      { type: "text", name: "serviceBooked", label: "Service Booked" },
      { type: "text", name: "amountPaidByClient", label: "Amount Paid by Client" },
      { type: "radio", name: "paymentCleared", label: "Payment Cleared?", options: yesNoPendingOptions },
      { type: "radio", name: "projectOfficiallyStarted", label: "Project Officially Started?", options: yesNoPendingOptions },
    ],
  },
  {
    id: "referral-payout",
    title: "Referral Payout",
    fields: [
      { type: "radio", name: "referralPayoutEligible", label: "Referral Payout Eligible?", options: ["Yes", "No", "Pending Review"] },
      { type: "text", name: "referralPercentage", label: "Referral Percentage", helperText: "Default value: 15%" },
      { type: "text", name: "payoutCap", label: "Payout Cap", helperText: "Default value: $200" },
      { type: "text", name: "calculatedPayoutAmount", label: "Calculated Payout Amount" },
      { type: "select", name: "payoutStatus", label: "Payout Status", required: true, options: ["Not Eligible Yet", "Pending", "Approved", "Paid", "Held", "Denied"] },
      { type: "date", name: "payoutDate", label: "Payout Date" },
      { type: "select", name: "payoutMethod", label: "Payout Method", options: ["Zelle", "Cash App", "PayPal", "Check", "Other"] },
      { type: "textarea", name: "payoutNotes", label: "Payout Notes", rows: 5, className: "md:col-span-2" },
    ],
  },
  {
    id: "internal-notes",
    title: "Internal Notes",
    fields: [
      { type: "radio", name: "followUpNeeded", label: "Follow-Up Needed?", options: yesNoOptions },
      { type: "date", name: "followUpDate", label: "Follow-Up Date" },
      { type: "textarea", name: "internalNotes", label: "Internal Notes", rows: 6, className: "md:col-span-2" },
      { type: "textarea", name: "specialAgreementNotes", label: "Special Agreement or Exception Notes", rows: 5, className: "md:col-span-2" },
    ],
  },
  {
    id: "confirmation",
    title: "Internal Confirmation",
    fields: [internalConfirmationField],
  },
];

export const partnerReferralTrackerFormConfig = adminForm({
  formKey: "admin-partner-referral-tracker",
  formPath: "/admin/partner-referral-tracker",
  formTitle: "Partner & Referral Tracker",
  setupTitle: "Partner & Referral Tracker",
  eyebrow: "Admin Only",
  heroHeading: "Partner & Referral Tracker",
  heroSubheading: "Track partner codes, referred clients, booking status, payout eligibility, payout amounts, and internal notes.",
  metaDescription: "Internal Kreatethedate partner and referral tracker form.",
  submitLabel: "Submit Partner Tracker Entry",
  requiredMessage: "Please complete all required fields before submitting the Partner & Referral Tracker.",
  thankYouMessage: "Your Kreatethedate Partner & Referral Tracker entry has been received.",
  subjectNameField: "referredClientName",
  clientNameField: "referredClientName",
  eventNameField: "referredServiceInterest",
  eventNameLabel: "Referred service interest",
  sections: partnerReferralTrackerSections,
});

const clientFeedbackSections: ConfiguredFormSection[] = [
  {
    id: "client-feedback",
    title: "Client Feedback",
    fields: [
      { type: "text", name: "clientName", label: "Client Name", required: true, autoComplete: "name" },
      { type: "text", name: "serviceCompleted", label: "Service Completed", required: true },
      { type: "text", name: "projectEventName", label: "Project/Event Name" },
      { type: "date", name: "deliveryDate", label: "Delivery Date" },
      { type: "select", name: "overallExperienceRating", label: "Overall Experience Rating", options: ["1", "2", "3", "4", "5"] },
      { type: "textarea", name: "lovedMost", label: "What did you love most?", rows: 5, className: "md:col-span-2" },
      { type: "textarea", name: "couldBeBetter", label: "What could have been better?", rows: 5, className: "md:col-span-2" },
      { type: "radio", name: "recommendKreatethedate", label: "Would you recommend Kreatethedate?", options: ["Yes", "No", "Maybe"] },
      {
        type: "radio",
        name: "testimonialPermission",
        label: "Testimonial Permission?",
        options: ["Yes, Kreatethedate may use my feedback publicly", "No, private feedback only", "Please contact me first"],
        className: "md:col-span-2",
      },
      { type: "textarea", name: "testimonialText", label: "Testimonial Text", rows: 6, className: "md:col-span-2" },
      { type: "textarea", name: "internalFollowUpNotes", label: "Internal Follow-Up Notes", rows: 5, className: "md:col-span-2" },
    ],
  },
];

export const clientFeedbackFormConfig = adminForm({
  formKey: "admin-client-feedback",
  formPath: "/admin/client-feedback",
  formTitle: "Client Feedback",
  setupTitle: "Client Feedback",
  eyebrow: "Admin Only",
  heroHeading: "Client Feedback",
  heroSubheading: "Record post-delivery client feedback, rating, testimonial permissions, and internal follow-up notes.",
  metaDescription: "Internal Kreatethedate client feedback form.",
  submitLabel: "Submit Client Feedback",
  requiredMessage: "Please complete all required fields before submitting Client Feedback.",
  thankYouMessage: "Your Kreatethedate Client Feedback has been received.",
  subjectNameField: "clientName",
  clientNameField: "clientName",
  eventNameField: "projectEventName",
  eventDateField: "deliveryDate",
  eventNameLabel: "Project/event name",
  eventDateLabel: "Delivery date",
  sections: clientFeedbackSections,
});

const generalSupportRequestSections: ConfiguredFormSection[] = [
  {
    id: "general-support-request",
    title: "General Support Request",
    fields: [
      { type: "text", name: "clientName", label: "Client Name", required: true, autoComplete: "name" },
      { type: "email", name: "clientEmail", label: "Email", required: true, autoComplete: "email" },
      {
        type: "select",
        name: "serviceRelatedTo",
        label: "Service Related To",
        options: [...publicServiceOptions, "Other"],
      },
      {
        type: "select",
        name: "requestType",
        label: "Request Type",
        options: ["General Question", "Technical Issue", "Link Issue", "Content Update", "Access Request", "Billing Question", "Other"],
      },
      { type: "textarea", name: "requestDetails", label: "Request Details", required: true, rows: 7, className: "md:col-span-2" },
      { type: "select", name: "priority", label: "Priority", options: supportPriorityOptions },
      { type: "radio", name: "followUpNeeded", label: "Follow-Up Needed?", options: yesNoOptions },
      { type: "date", name: "followUpDate", label: "Follow-Up Date" },
      { type: "textarea", name: "internalNotes", label: "Internal Notes", rows: 6, className: "md:col-span-2" },
    ],
  },
];

export const generalSupportRequestFormConfig = adminForm({
  formKey: "admin-general-support-request",
  formPath: "/admin/general-support-request",
  formTitle: "General Support Request",
  setupTitle: "General Support Request",
  eyebrow: "Admin Only",
  heroHeading: "General Support Request",
  heroSubheading: "Track internal support requests, issue type, service area, priority, follow-up needs, and notes.",
  metaDescription: "Internal Kreatethedate general support request form.",
  submitLabel: "Submit Support Request",
  requiredMessage: "Please complete all required fields before submitting the General Support Request.",
  thankYouMessage: "Your Kreatethedate General Support Request has been received.",
  subjectNameField: "clientName",
  clientNameField: "clientName",
  eventNameField: "serviceRelatedTo",
  eventNameLabel: "Service related to",
  sections: generalSupportRequestSections,
});

export const adminServiceFormConfigs = [
  discoveryCallNotesFormConfig,
  kickoffCallPrepFormConfig,
  clientDirectoryFormConfig,
  projectDashboardFormConfig,
  partnerReferralTrackerFormConfig,
  clientFeedbackFormConfig,
  generalSupportRequestFormConfig,
];

export const adminFormsDirectoryEntries: AdminFormsDirectoryEntry[] = [
  {
    formName: "RSVP Hub Form",
    category: "Client-Facing Forms",
    routeUrl: "/rsvp-hub",
    visibility: "Public Client Service Form",
    purpose: "Collect event, RSVP, design, upload, deadline, and confirmation details for an RSVP Hub.",
  },
  {
    formName: "Memory Hub Form",
    category: "Client-Facing Forms",
    routeUrl: "/memory-hub",
    visibility: "Public Client Service Form",
    purpose: "Collect event memories, media, privacy, display, and creative direction for a Memory Hub.",
  },
  {
    formName: "Archive Hub Form",
    category: "Client-Facing Forms",
    routeUrl: "/archive-hub",
    visibility: "Public Client Service Form",
    purpose: "Collect timeline, legacy, media, design, and preservation goals for an Archive Hub.",
  },
  {
    formName: "Memory Media Vault Form",
    category: "Client-Facing Forms",
    routeUrl: "/memory-media-vault",
    visibility: "Public Client Service Form",
    purpose: "Collect premium vault setup, contributor, media, organization, privacy, and permission details.",
  },
  {
    formName: "Signature Song Form",
    category: "Client-Facing Forms",
    routeUrl: "/signature-song",
    visibility: "Public Client Service Form",
    purpose: "Collect story, tone, music direction, inspiration, and confirmation details for a custom song.",
  },
  {
    formName: "Signature Highlight Reel Form",
    category: "Client-Facing Forms",
    routeUrl: "/signature-highlight-reel",
    visibility: "Public Client Service Form",
    purpose: "Collect event, footage, media, style, and delivery details for a highlight reel.",
  },
  {
    formName: "Partner Referral Form",
    category: "Private Forms",
    routeUrl: "/partner-referral",
    visibility: "Private Direct Link",
    purpose: "Collect referral details from partners, vendors, planners, and collaborators.",
  },
  {
    formName: "Proposal Dashboard",
    category: "Admin Workflow",
    routeUrl: "/admin/proposal-dashboard",
    visibility: "Admin Only",
    purpose: "Review proposal, agreement, payment, and scheduling workflow status from the private admin hub.",
  },
  {
    formName: "Executive Dashboard",
    category: "Admin Workflow",
    routeUrl: "/admin/executive-dashboard",
    visibility: "Admin Only",
    purpose: "View revenue, pipeline, lead source analytics, referrals, capacity, and executive KPI reporting.",
  },
  {
    formName: "Create Proposal",
    category: "Admin Workflow",
    routeUrl: "/admin/proposals",
    visibility: "Admin Only",
    purpose: "Create and manage Kreatethedate proposals, selected services, package pricing, add-ons, and proposal links.",
  },
  {
    formName: "Agreement Records",
    category: "Admin Workflow",
    routeUrl: "/admin/agreements",
    visibility: "Admin Only",
    purpose: "Review signed agreements, agreement status, signature details, selected services, and internal agreement records.",
  },
  {
    formName: "Payment Records",
    category: "Admin Workflow",
    routeUrl: "/admin/payments",
    visibility: "Admin Only",
    purpose: "Review agreement payment status, generate payment links, and track payment readiness from service agreement records.",
  },
  {
    formName: "Scheduler Settings",
    category: "Admin Workflow",
    routeUrl: "/admin/scheduler",
    visibility: "Admin Only",
    purpose: "Configure scheduling links, scheduler requirements, kickoff call status, and admin scheduling follow-up.",
  },
  {
    formName: "Welcome Email / Onboarding Settings",
    category: "Admin Workflow",
    routeUrl: "/admin/welcome-email-settings",
    visibility: "Admin Only",
    purpose: "Manage kickoff scheduling settings used by conditional welcome email and onboarding next-step workflows.",
  },
  {
    formName: "Discovery Call Notes",
    category: "Admin Forms",
    routeUrl: "/admin/discovery-call-notes",
    visibility: "Admin Only",
    purpose: "Record internal discovery call notes, goals, concerns, recommendations, and outcomes.",
  },
  {
    formName: "Kickoff Call Prep",
    category: "Admin Forms",
    routeUrl: "/admin/kickoff-call-prep",
    visibility: "Admin Only",
    purpose: "Prepare internal kickoff checklist, missing information, creative direction, and risks.",
  },
  {
    formName: "Client Directory Entry",
    category: "Admin Forms",
    routeUrl: "/admin/client-directory",
    visibility: "Admin Only",
    purpose: "Record internal client contact, project, status, folder, form, kickoff, and deadline details.",
  },
  {
    formName: "Project Dashboard Entry",
    category: "Admin Forms",
    routeUrl: "/admin/project-dashboard",
    visibility: "Admin Only",
    purpose: "Track project status, priority, assignments, due dates, next steps, and client dependencies.",
  },
  {
    formName: "Partner & Referral Tracker",
    category: "Admin Forms",
    routeUrl: "/admin/partner-referral-tracker",
    visibility: "Admin Only",
    purpose: "Track partner codes, referral status, booking status, payout eligibility, and payout status.",
  },
  {
    formName: "Client Feedback",
    category: "Admin Forms",
    routeUrl: "/admin/client-feedback",
    visibility: "Admin Only",
    purpose: "Record project feedback, ratings, recommendation status, testimonials, and follow-up notes.",
  },
  {
    formName: "General Support Request",
    category: "Admin Forms",
    routeUrl: "/admin/general-support-request",
    visibility: "Admin Only",
    purpose: "Track support questions, technical issues, content updates, access requests, and priority.",
  },
];
